// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatTime } from "@/lib/dates";

const money = (cents?: number | null) =>
  typeof cents === "number" ? `$${(cents / 100).toFixed(2)}` : "—";

const dateLabel = (d?: string | null) => {
  if (!d) return "Date TBD";
  const [y, mo, dy] = d.split("-").map(Number);
  return new Date(y, mo - 1, dy).toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
};

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    assigned: { label: "Needs response", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
    accepted: { label: "Accepted", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
    declined: { label: "Declined", cls: "bg-muted text-muted-foreground border-border" },
  };
  const s = map[status] ?? { label: status, cls: "bg-muted text-muted-foreground border-border" };
  return (
    <span className={cn("rounded-full border px-2.5 py-0.5 text-xs font-semibold", s.cls)}>
      {s.label}
    </span>
  );
}

export default function MyOfficiatingSection() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [availability, setAvailability] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState({ date: "", start: "", end: "", note: "" });

  const officialId = rows[0]?.official_id ?? null;

  const loadSchedule = useCallback(async () => {
    const { data, error } = await supabase.rpc("get_official_schedule");
    if (error) {
      setRows([]);
      return null;
    }
    const list = Array.isArray(data) ? data : [];
    setRows(list);
    return list[0]?.official_id ?? null;
  }, []);

  const loadAvailability = useCallback(async (id: string | null) => {
    if (!id) return setAvailability([]);
    const { data, error } = await supabase.rpc("get_official_availability", { p_official_id: id });
    if (!error) setAvailability(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const id = await loadSchedule();
      await loadAvailability(id);
      setLoading(false);
    })();
  }, [loadSchedule, loadAvailability]);

  const respond = async (assignmentId: string, accept: boolean) => {
    setBusy(assignmentId);
    const { error } = await supabase.rpc("official_respond", {
      p_game_official_id: assignmentId,
      p_accept: accept,
    });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success(accept ? "Assignment accepted" : "Assignment declined");
    await loadSchedule();
  };

  const addAvailability = async () => {
    if (!officialId) return;
    if (!form.date || !form.start || !form.end) return toast.error("Date, start and end time are required");
    setBusy("availability");
    const { error } = await supabase.rpc("set_official_availability", {
      p_official_id: officialId,
      p_available_date: form.date,
      p_start_time: form.start,
      p_end_time: form.end,
      p_is_available: true,
      p_notes: form.note || null,
    });
    setBusy(null);
    if (error) return toast.error(error.message);
    setForm({ date: "", start: "", end: "", note: "" });
    await loadAvailability(officialId);
  };

  const removeAvailability = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.rpc("delete_official_availability", { p_availability_id: id });
    setBusy(null);
    if (error) return toast.error(error.message);
    await loadAvailability(officialId);
  };

  const groups = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const r of rows) {
      const key = r.scheduled_date || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="flex items-center gap-2 text-xl font-bold leading-tight">
          My officiating
        </h2>
        <p className="text-sm text-muted-foreground">
          These are the games you've been assigned to officiate. Accept or decline each one so the
          tournament director knows your status.
        </p>
      </div>

      <div className="space-y-5">
        {groups.map(([date, items]) => (
          <div key={date} className="space-y-2">
            <p className="text-sm font-semibold text-muted-foreground">{dateLabel(date)}</p>
            <div className="space-y-3">
              {items.map((g) => (
                <div key={g.assignment_id} className="space-y-2 rounded-2xl border bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-semibold">
                      {formatTime(g.scheduled_start_time, "TBD")} · {g.court_name || "Court TBD"}
                    </div>
                    <StatusPill status={g.status} />
                  </div>
                  <div className="text-sm font-medium">
                    {g.team_a_name || "TBD"} vs {g.team_b_name || "TBD"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[g.division, g.event_name].filter(Boolean).join(" · ")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Position: {g.position || "—"} · Fee: {money(g.game_fee_cents)}
                  </div>
                  {g.status === "assigned" && (
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        disabled={busy === g.assignment_id}
                        onClick={() => respond(g.assignment_id, true)}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === g.assignment_id}
                        onClick={() => respond(g.assignment_id, false)}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3 rounded-2xl border bg-card p-4">
        <h3 className="text-base font-semibold">Availability</h3>
        {availability.length === 0 ? (
          <p className="text-sm text-muted-foreground">No availability windows added yet.</p>
        ) : (
          <ul className="space-y-2">
            {availability.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between gap-3 rounded-xl bg-muted/50 px-3 py-2 text-sm"
              >
                <span>
                  {dateLabel(a.available_date)} · {formatTime(a.start_time, "TBD")} – {formatTime(a.end_time, "TBD")}
                  {a.notes ? ` · ${a.notes}` : ""}
                </span>
                <button
                  type="button"
                  aria-label="Remove availability"
                  className="text-muted-foreground hover:text-destructive"
                  disabled={busy === a.id}
                  onClick={() => removeAvailability(a.id)}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="grid gap-2 sm:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Start</Label>
            <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">End</Label>
            <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Note (optional)</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <Button size="sm" onClick={addAvailability} disabled={busy === "availability" || !officialId}>
          {busy === "availability" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add availability
        </Button>
      </div>
    </section>
  );
}
